import { PwaLocale, pwaLocales } from './locales/pwa-locales';
import {
  NoWebTargetPageDescription,
  PageDescription,
  PwaUrlBuilder,
  Url,
  WwwUrlBuilder,
} from './url-builder';

/**
 * Describes an app-only feature that is reachable via a native deeplink but has
 * no direct web equivalent. See {@link deepLinkProductsWithoutWebTarget}.
 */
export interface DeepLinkProductWithoutWebTarget {
  /**
   * The native app deeplink path, e.g. `'recorder'` which the deep-link builder
   * turns into `wetteronline://deeplink.to/recorder`.
   */
  deeplink: string;

  /**
   * The web target used for every locale that is not listed in
   * {@link localeExceptions}. Typically the generic "discover the app" page.
   */
  webTarget: NoWebTargetPageDescription;

  /**
   * Per-locale overrides of {@link webTarget}, e.g. a dedicated product
   * marketing article for `de-DE`.
   */
  localeExceptions?: Partial<Record<PwaLocale, NoWebTargetPageDescription>>;
}

/**
 * Registry of links that have a deeplink in the native apps but no web
 * equivalent.
 *
 * Used in purple as technical placeholder URLs (the record key is the
 * URL's path segment, e.g. `temporary-skyclip` for
 * `https://www.weatherandradar.co.uk/temporary-skyclip`). The placeholder URL
 * is a pure marker and is never delivered to users:
 * - the deep-link parser recognizes it and produces a
 *   `NoWebTargetProductParsed` carrying the key,
 * - the deep-link builder uses {@link DeepLinkProductWithoutWebTarget.deeplink}
 *   to build the native app deeplink,
 * - the frontend hyperlink transformers use {@link resolveNoWebTarget} to build
 *   the correct region-specific web URL at render time.
 *
 * Adding a new app-only feature only requires a new entry here and for ticker apps & www a change in hyperlink-transformer.ts.
 */
export const deepLinkProductsWithoutWebTarget: Record<
  string,
  DeepLinkProductWithoutWebTarget
> = {
  'temporary-skyclip': {
    deeplink: 'recorder',
    // Everywhere except de-DE: the generic "discover the app" page.
    webTarget: { recipe: 'productMarketingRoot' },
    localeExceptions: {
      // de-DE gets a dedicated product marketing article.
      'de-DE': {
        recipe: 'productMarketingArticle',
        params: { postId: '01e24c5d-ecfa-4547-8c49-d5e564226ff3' },
      },
    },
  },
};

/** Returns the registry entry for a placeholder path, if any. */
export function getNoWebTargetProduct(
  key: string,
): DeepLinkProductWithoutWebTarget | undefined {
  return deepLinkProductsWithoutWebTarget[key];
}

/**
 * Resolves the web target for a placeholder key and locale, applying any
 * locale-specific exception. Returns `undefined` if the key is unknown.
 */
export function resolveNoWebTarget(
  key: string,
  locale: PwaLocale,
): NoWebTargetPageDescription | undefined {
  const entry = getNoWebTargetProduct(key);
  if (!entry) {
    return undefined;
  }
  return entry.localeExceptions?.[locale] ?? entry.webTarget;
}

/**
 * Builds an absolute web URL for a placeholder link, so the technical
 * placeholder URL is never exposed to a user as a fallback (e.g. via the
 * `defaultUri` of a parsed link).
 *
 * Returns the resolved web target URL if it can be built for the given locale,
 * otherwise the locale's home page. Returns `undefined` only if not even the
 * home page can be built (should not happen for supported locales).
 */
export function resolveNoWebTargetUrl(
  key: string,
  locale: string,
): string | undefined {
  const builder = (pwaLocales as readonly string[]).includes(locale)
    ? PwaUrlBuilder.fromLocale(locale)
    : WwwUrlBuilder.fromLocale(locale);

  const toAbsolute = (url: Url): string =>
    `https://${builder.subDomain}.${builder.hostname}${url.fullUrl}`;

  const webTarget = resolveNoWebTarget(key, locale as PwaLocale);
  if (webTarget) {
    try {
      return toAbsolute(
        builder.build({
          recipe: webTarget.recipe,
          ...webTarget.params,
        } as PageDescription),
      );
    } catch {
      // Web target not buildable for this locale - fall back to the home page.
    }
  }

  try {
    return toAbsolute(builder.build({ recipe: 'home' }));
  } catch {
    return undefined;
  }
}
